import React, { useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  redirect,
  useParams,
} from "react-router-dom";
import Sidebar from "./layout/Sidebar";
import { MovementRegistryProvider } from "./contexts/MovementRegistryContext";
import { MovementStateProvider } from "./contexts/MovementStateContext";
import ViewWorkoutProgramme, { loader as WorkoutProgramLoader } from "./pages/ViewWorkoutProgramme";
import RouteErrorCard from "./components/RouteErrorCard";
import { useTrainingHome } from "./hooks/useTrainingHome";
import { useUserAccess } from "./hooks/useUserAccess";
import {
  firebaseAuth,
  firebaseFunctions,
  useAuthContext,
  type CreateCrossDomainTokenResult,
} from "@lemonadetraining/web-shared";
import Notification from "./components/Notification";
import { Toaster } from "@lemonadetraining/web-ui";
import CheckoutModal from "@lemonadetraining/web-shared/components/CheckoutModal";
import FloatingCart from "@lemonadetraining/web-shared/components/FloatingCart";
import CookieNotice from "@lemonadetraining/web-shared/components/CookieNotice";
import AuthenticatingScreen from "./components/AuthenticatingScreen";
import AccessEndedScreen from "./components/access/AccessEndedScreen";
import { readAccessEndedMarker } from "@lemonadetraining/web-shared/utils/accessEndedMarker";
import { useTimeFormatSync } from "./hooks/useTimeFormatSync";
import { onAuthStateChanged, signInWithCustomToken, type User } from "firebase/auth";
import { httpsCallable } from "firebase/functions";

// ---- [Pages]
import HomePage from "./pages/_dashboard/HomePage";
import MyTrainingPage from "./pages/_dashboard/MyTrainingPage";
import ProfilePage from "./pages/_dashboard/ProfilePage";
import WorkoutLibrary from "./pages/_dashboard/WorkoutLibrary";

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";

const waitForAuthInit = (): Promise<User | null> => {
  return new Promise((resolve) => {
    // If user is already available, resolve immediately
    if (firebaseAuth.currentUser) {
      console.log("[waitForAuthInit] User already available");
      resolve(firebaseAuth.currentUser);
      return;
    }

    // Set a longer timeout for cross-domain auth checks
    const timeout = setTimeout(() => {
      unsubscribe();
      console.log("[waitForAuthInit] Timeout reached, no user found");
      resolve(null);
    }, 15000); // 15 second max wait for cross-domain auth

    let hasResolved = false;
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (hasResolved) return;
      hasResolved = true;
      clearTimeout(timeout);
      unsubscribe();
      // Log presence only — the account email is PII and never belongs in logs.
      console.log("[waitForAuthInit] Auth state resolved:", user ? "User found" : "No user");
      resolve(user);
    });

    // Also check if there's a stored token from cross-domain login
    const storedToken = sessionStorage.getItem("firebase:authToken");
    if (storedToken) {
      console.log("[waitForAuthInit] Found stored token, Firebase Auth should detect session");
      // Firebase Auth should automatically detect this via onAuthStateChanged
      // But we'll wait for it to resolve
    }
  });
};

// Auth Guard component - checks auth and redirects if needed
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const authCtx = useAuthContext();
  const [hasChecked, setHasChecked] = React.useState(false);
  const [isWaitingForAuth, setIsWaitingForAuth] = React.useState(true);
  const [firebaseUserDetected, setFirebaseUserDetected] = React.useState(false);
  const hasRedirected = React.useRef(false);

  // Check if coming from login redirect
  const urlParams = React.useMemo(() => new URLSearchParams(window.location.search), []);
  const fromLogin = urlParams.get("fromLogin") === "true";

  // Set when a gym removed this person and the server revoked their token.
  // Re-read whenever the session drops rather than once at mount: this guard
  // wraps the whole dashboard layout and does NOT remount on a forced
  // sign-out, so a mount-time read would always run before the marker exists.
  const [accessEnded, setAccessEnded] = React.useState(() => readAccessEndedMarker());
  const signedIn = Boolean(authCtx.user) || firebaseUserDetected;

  React.useEffect(() => {
    if (signedIn) return;
    setAccessEnded(readAccessEndedMarker());
  }, [signedIn]);

  // Listen for Firebase Auth state changes
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
      if (user) {
        // Force token refresh to ensure session is fully established
        try {
          await user.getIdToken(true); // Force refresh
          setFirebaseUserDetected(true);
          setIsWaitingForAuth(false);
          setHasChecked(true);
        } catch (error) {
          console.error("[AuthGuard] Error refreshing token:", error);
          // Still allow access if user exists, token refresh might not be critical
          setFirebaseUserDetected(true);
          setIsWaitingForAuth(false);
          setHasChecked(true);
        }
      } else {
        // Only set to false if we've waited long enough
        if (hasChecked) {
          setFirebaseUserDetected(false);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [hasChecked]);

  React.useEffect(() => {
    // Wait longer if coming from login (cross-domain auth via shared authDomain takes time)
    const initialWait = fromLogin ? 5000 : 1000;

    const timer = setTimeout(() => {
      setHasChecked(true);
    }, initialWait);

    return () => clearTimeout(timer);
  }, [fromLogin]);

  React.useEffect(() => {
    if (!hasChecked) return;

    // Check if user is logging out - skip all auth checks
    const isLoggingOut = sessionStorage.getItem("isLoggingOut") === "true";
    if (isLoggingOut) {
      return;
    }

    // A removal explains itself first; the redirect waits for their Sign in.
    if (accessEnded) {
      setIsWaitingForAuth(false);
      return;
    }

    // If authenticated via AuthContext, we're good
    if (authCtx.isLoggedIn && authCtx.user) {
      setIsWaitingForAuth(false);
      return;
    }

    // If Firebase Auth has detected a user, allow access
    if (firebaseAuth.currentUser || firebaseUserDetected) {
      setIsWaitingForAuth(false);
      return;
    }

    // Wait longer for auth state to initialize, especially after cross-domain redirect
    // Firebase Auth needs to check the server for existing sessions
    const waitTime = fromLogin ? 15000 : 5000; // 15 seconds if from login, 5 otherwise

    const redirectTimer = setTimeout(() => {
      // Check again if logging out (user might have clicked logout during wait)
      const isLoggingOutNow = sessionStorage.getItem("isLoggingOut") === "true";
      if (isLoggingOutNow) {
        return;
      }

      // Only redirect if we're absolutely sure there's no user
      if (
        !authCtx.isLoggedIn &&
        !authCtx.user &&
        !firebaseAuth.currentUser &&
        !firebaseUserDetected &&
        !hasRedirected.current
      ) {
        // Last look before we leave: on the revoked-token path the SDK signs
        // out before the error surfaces, so the marker can be written after
        // the effect above already read for it.
        const lateMarker = readAccessEndedMarker();
        if (lateMarker) {
          setAccessEnded(lateMarker);
          setIsWaitingForAuth(false);
          return;
        }

        hasRedirected.current = true;
        setIsWaitingForAuth(false);

        const websiteUrl = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";
        const appUrl = process.env.REACT_APP_APP_URL || "http://localhost:3001";
        const returnUrl = `${appUrl}/dashboard`;
        window.location.href = `${websiteUrl}/login?returnUrl=${encodeURIComponent(returnUrl)}`;
      } else if (firebaseAuth.currentUser || firebaseUserDetected) {
        setIsWaitingForAuth(false);
      }
    }, waitTime);

    return () => clearTimeout(redirectTimer);
  }, [authCtx.isLoggedIn, authCtx.user, hasChecked, firebaseUserDetected, fromLogin, accessEnded]);

  // Check if user is logging out - don't show error, just blank screen
  const isLoggingOut = sessionStorage.getItem("isLoggingOut") === "true";

  // Safety: If flag is set but user is authenticated, clear the stale flag
  if (isLoggingOut && (firebaseAuth.currentUser || firebaseUserDetected)) {
    sessionStorage.removeItem("isLoggingOut");
  } else if (isLoggingOut) {
    return null; // Render nothing during logout redirect
  }

  // Show loading while checking or waiting for auth
  if (!hasChecked || isWaitingForAuth) {
    return <AuthenticatingScreen />;
  }

  // If authenticated via AuthContext, show content
  if (authCtx.isLoggedIn && authCtx.user) {
    return children;
  }

  // If Firebase Auth has detected a user (even if AuthContext hasn't synced yet), show content
  if (firebaseAuth.currentUser || firebaseUserDetected) {
    return children;
  }

  // Signed out because a gym removed them: say so rather than looping through
  // a login screen that will not explain it.
  if (accessEnded) {
    return (
      <AccessEndedScreen
        gymName={accessEnded.gymName}
        onSignIn={() => {
          const websiteUrl = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";
          const appUrl = process.env.REACT_APP_APP_URL || "http://localhost:3001";
          const returnUrl = `${appUrl}/dashboard`;
          window.location.href = `${websiteUrl}/login?returnUrl=${encodeURIComponent(returnUrl)}`;
        }}
      />
    );
  }

  // Still checking authentication
  return <AuthenticatingScreen />;
};

const router = createBrowserRouter([
  {
    path: "/",
    loader: async () => {
      throw redirect("/dashboard/");
    },
  },
  {
    path: "/dashboard",
    loader: async () => {
      throw redirect("/dashboard/");
    },
  },
  {
    // Short URL used by the mobile app's "Review on web" link.
    path: "/requests",
    loader: async () => {
      throw redirect("/dashboard/athletes?tab=requests");
    },
  },
  {
    path: "dashboard/*",
    children: [
      {
        element: (
          <AuthGuard>
            <MovementRegistryProvider>
              <MovementStateProvider>
                <Sidebar />
              </MovementStateProvider>
            </MovementRegistryProvider>
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <HomePage />,
          },
          {
            path: "today",
            async lazy() {
              const TodayPage = await import("./pages/_dashboard/TodayPage.tsx");
              return { Component: TodayPage.default };
            },
          },
          // F032: My training at its own address, so staff who land on Today
          // still reach it.
          { path: "training", element: <MyTrainingPage /> },
          // Stale bookmark: redirect here rather than via the catch-all, which
          // waits up to 2s on auth before deciding.
          { path: "shopping-cart", loader: () => redirect("/dashboard/") },
          { path: "workout-library", element: <WorkoutLibrary /> },
          {
            path: "movements/:movementId",
            async lazy() {
              const MovementDetailPage = await import("./pages/_dashboard/MovementDetailPage.tsx");
              return { Component: MovementDetailPage.default };
            },
          },
          // F032 step 9: today's workouts live on My training › Overview.
          { path: "todays-workout", element: <TrainingHomeRedirect /> },
          {
            path: "box-portal",
            async lazy() {
              const BoxPortalPage = await import("./pages/_dashboard/BoxPortalPage.tsx");
              return { Component: BoxPortalPage.default };
            },
          },
          {
            path: "coach-portal",
            async lazy() {
              const CoachPortalPage = await import("./pages/_dashboard/CoachPortalPage.tsx");
              return { Component: CoachPortalPage.default };
            },
          },
          {
            path: "programming",
            async lazy() {
              const ProgrammingPage = await import("./pages/_dashboard/ProgrammingPage.tsx");
              return { Component: ProgrammingPage.default };
            },
          },
          {
            path: "programming/new",
            async lazy() {
              const NewProgramPage = await import("./pages/_dashboard/NewProgramPage.tsx");
              return { Component: NewProgramPage.default };
            },
          },
          {
            path: "setup",
            async lazy() {
              const SetupGuidePage = await import("./pages/_dashboard/SetupGuidePage.tsx");
              return { Component: SetupGuidePage.default };
            },
          },
          {
            path: "team",
            async lazy() {
              const TeamPage = await import("./pages/_dashboard/TeamPage.tsx");
              return { Component: TeamPage.default };
            },
          },
          {
            path: "gym-settings",
            async lazy() {
              const GymSettingsPage = await import("./pages/_dashboard/GymSettingsPage.tsx");
              return { Component: GymSettingsPage.default };
            },
          },
          {
            // F032 step 8c. Never /dashboard/memberships: live emails link there.
            path: "gym-memberships",
            async lazy() {
              const MembershipsPage =
                await import("./pages/_dashboard/Memberships/MembershipsPage.tsx");
              return { Component: MembershipsPage.default };
            },
          },
          {
            path: "gym-memberships/new",
            async lazy() {
              const NewMembershipPage =
                await import("./pages/_dashboard/Memberships/NewMembershipPage.tsx");
              return { Component: NewMembershipPage.default };
            },
          },
          {
            path: "gym-memberships/:productID",
            async lazy() {
              const MembershipPage =
                await import("./pages/_dashboard/Memberships/MembershipPage.tsx");
              return { Component: MembershipPage.default };
            },
          },
          {
            path: "athletes",
            async lazy() {
              const MembersPage = await import("./pages/_dashboard/MembersPage.tsx");
              return { Component: MembersPage.default };
            },
          },
          {
            // F032: Requests is a tab of Members; old links and emails land there.
            path: "requests",
            loader: async () => {
              throw redirect("/dashboard/athletes?tab=requests");
            },
          },
          {
            // Splat: the flow owns its own sub-steps (/reason, /confirm,
            // /lane-a, /lane-b) so each is a real URL a member can be sent
            // back to, and back/forward move between them.
            path: "membership/:subscriptionId/cancel/*",
            async lazy() {
              const CancelMembershipFlowPage =
                await import("./pages/_dashboard/CancelMembershipFlowPage.tsx");
              return { Component: CancelMembershipFlowPage.default };
            },
          },
          { path: "memberships", loader: () => redirect("/dashboard/profile?tab=memberships") },
          { path: "invites", loader: () => redirect("/dashboard/profile?tab=invitations") },
          { path: "profile", element: <ProfilePage /> },
          {
            path: "admin-portal",
            async lazy() {
              const AdminPortal = await import("./pages/_dashboard/AdminPortal.tsx");
              return { Component: AdminPortal.default };
            },
          },
          {
            path: "admin/movements",
            async lazy() {
              const MovementRegistryPage =
                await import("./pages/_dashboard/MovementRegistryPage.tsx");
              return { Component: MovementRegistryPage.default };
            },
          },
          {
            path: "workout-programme/:UID",
            element: <ViewWorkoutProgramme />,
            loader: WorkoutProgramLoader,
            // A failed load stays inside the dashboard, with a way back (LT-2013).
            errorElement: <RouteErrorCard what="program" />,
          },
          // Booking System - Admin/Coach Portal Routes
          {
            // The Booking-admin home: Class Schedule (default) / Scheduling
            // Templates / Class Types / Settings tabs.
            path: "box-portal/booking",
            async lazy() {
              const BookingManagementPage =
                await import("./pages/_dashboard/Booking/BookingManagementPage.tsx");
              return { Component: BookingManagementPage.default };
            },
          },
          {
            // Legacy home of Class Types — now a Booking Management tab. The redirect
            // keeps old bookmarks and deep links working.
            path: "box-portal/class-types",
            loader: () => redirect("/dashboard/box-portal/booking?tab=class-types"),
          },
          {
            // Legacy home of the Class Schedule — now the Booking Management default
            // tab. The redirect keeps old bookmarks working and carries the filter
            // params (?classTypes=…&coaches=…) across, since the schedule grid syncs
            // its filters to the URL.
            path: "box-portal/classes/schedule",
            loader: ({ request }: { request: Request }) => {
              const url = new URL(request.url);
              return redirect(`/dashboard/box-portal/booking${url.search}`);
            },
          },
          {
            // Legacy home of the templates library — now the Scheduling Templates tab
            // of Booking Management. Static segment still precedes the ":classID"
            // dynamic route below so the old link is never captured as a class id.
            path: "box-portal/classes/templates",
            loader: () => redirect("/dashboard/box-portal/booking?tab=templates"),
          },
          {
            // From-scratch template authoring — a deeper path than "classes/:classID"
            // (which is one segment), so it is never captured by that dynamic route.
            path: "box-portal/classes/templates/new",
            async lazy() {
              const TemplateEditorPage =
                await import("./pages/_dashboard/Booking/TemplateEditorPage.tsx");
              return { Component: TemplateEditorPage.default };
            },
          },
          {
            path: "box-portal/classes/templates/:templateID/edit",
            async lazy() {
              const TemplateEditorPage =
                await import("./pages/_dashboard/Booking/TemplateEditorPage.tsx");
              return { Component: TemplateEditorPage.default };
            },
          },
          // F032 8a: a class opens in the panel beside the week; old links land there.
          {
            path: "box-portal/classes/:classID",
            element: <ClassDetailsRedirect />,
          },
          // Booking System - Athlete Portal Routes
          {
            // The athlete Classes home: Schedule (default) / My Bookings tabs.
            path: "classes",
            async lazy() {
              const ClassesPage = await import("./pages/_dashboard/Booking/ClassesPage.tsx");
              return { Component: ClassesPage.default };
            },
          },
          {
            // Legacy home of the athlete schedule — now the Classes default tab.
            path: "classes/schedule",
            loader: () => redirect("/dashboard/classes"),
          },
          {
            path: "classes/:classID/book",
            async lazy() {
              const BookingConfirmationPage =
                await import("./pages/_dashboard/Booking/BookingConfirmationPage.tsx");
              return { Component: BookingConfirmationPage.default };
            },
          },
          {
            // Legacy home of My Bookings — now a Classes tab. Its old internal
            // ?tab=waitlist deep link maps to the new ?view= param.
            path: "classes/my-bookings",
            loader: ({ request }: { request: Request }) => {
              const url = new URL(request.url);
              const waitlist = url.searchParams.get("tab") === "waitlist";
              return redirect(
                `/dashboard/classes?tab=my-bookings${waitlist ? "&view=waitlist" : ""}`
              );
            },
          },
          {
            // Legacy waitlist shortcut (predates the My Bookings tabs).
            path: "classes/waitlist",
            loader: () => redirect("/dashboard/classes?tab=my-bookings&view=waitlist"),
          },
          {
            // Must live INSIDE this layout: the parent `dashboard/*` splat matches
            // every /dashboard/... URL, so the top-level `*` route can never fire,
            // and an unmatched child leaves this branch rendering a blank Outlet.
            path: "*",
            loader: () => redirect("/dashboard/"),
          },
        ],
      },
    ],
  },
  {
    path: "payment-status",
    async lazy() {
      const PaymentStatus = await import("./pages/PaymentStatus.tsx");
      return { Component: PaymentStatus.default };
    },
  },
  {
    path: "*",
    loader: async () => {
      // If user tries to access a non-existent route, redirect to dashboard if authenticated
      const user = await Promise.race([
        waitForAuthInit(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000)),
      ]);

      if (user) {
        throw redirect("/dashboard");
      } else {
        const websiteUrl = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";
        window.location.href = `${websiteUrl}/login`;
        throw redirect("/login");
      }
    },
    element: <div>Page not found</div>,
  },
]);

function App() {
  useTimeFormatSync();

  useEffect(() => {
    // Check for auth token in URL (from cross-domain login redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get("authToken");

    if (authToken) {
      // Call Cloud Function to verify ID token and create custom token
      const createCrossDomainToken = httpsCallable<
        { idToken: string },
        CreateCrossDomainTokenResult
      >(firebaseFunctions, "createCrossDomainToken");

      createCrossDomainToken({ idToken: authToken })
        .then(async (result) => {
          const { customToken } = result.data;

          // Sign in with the custom token
          try {
            await signInWithCustomToken(firebaseAuth, customToken);

            // Remove token from URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete("authToken");
            newUrl.searchParams.delete("fromLogin");
            window.history.replaceState({}, "", newUrl.toString());
          } catch (error) {
            console.error("Error signing in with custom token:", error);
            // Remove token from URL even on error
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete("authToken");
            newUrl.searchParams.delete("fromLogin");
            window.history.replaceState({}, "", newUrl.toString());
          }
        })
        .catch((error) => {
          console.error("Error during cross-domain authentication:", error);
          // Remove token from URL even on error
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete("authToken");
          newUrl.searchParams.delete("fromLogin");
          window.history.replaceState({}, "", newUrl.toString());
        });
    }
  }, []);

  return (
    <>
      {/* Mounted outside the router, so these also render on the unauthenticated
       * payment-status and not-found routes. Safe only because cart state is
       * in-memory: persisting it would turn those routes into a cart-contents
       * disclosure on a shared gym computer. */}
      <CheckoutModal />
      {/* Cleared of the bottom-right Toaster below: the "Added to cart" toast
       * fires as the button appears and would sit on top of it. */}
      <FloatingCart className="bottom-24 md:bottom-28" />
      <Toaster position="bottom-right" richColors closeButton duration={5000} />
      <Notification />
      <RouterProvider router={router} />
      <CookieNotice
        detailsHref={`${WEBSITE_URL}/privacy-policy#cookies`}
        opensInNewTab
        side="right"
      />
    </>
  );
}

function TrainingHomeRedirect() {
  const access = useUserAccess();
  const home = useTrainingHome();
  if (access.isLoading) return null;
  return <Navigate to={home} replace />;
}

function ClassDetailsRedirect() {
  const { classID } = useParams();
  return (
    <Navigate
      to={`/dashboard/box-portal/booking?class=${encodeURIComponent(classID ?? "")}`}
      replace
    />
  );
}

export default App;
